import {
  Box,
  BoxProps,
  ButtonGroup,
  HStack,
  Icon,
  IconButton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { useState } from 'react';
import {
  RiCheckboxBlankCircleLine,
  RiCheckboxCircleLine,
  RiDeleteBinLine,
  RiEyeLine,
} from 'react-icons/ri';
import { ConfirmationModal } from '../../../../../../../../components/ConfirmationModal';
import { LinkButton } from '../../../../../../../../components/LinkButton';
import { Pagination } from '../../../../../../../../components/Pagination';
import { TableFilters } from '../../../../../../../../components/TableFilters';
import { useAuth } from '../../../../../../../../hooks/auth';
import { IProductBase } from '../../../../../../../../models/products';
import deleteProductsService from '../../../../../../../../services/Products/DeleteProductsService';
import { translateError } from '../../../../../../../../utils/errors';

export interface IProductTableItem extends IProductBase {
  formattedCreatedAt: string;
}

interface IProductTableProps extends BoxProps {
  products: IProductTableItem[];
  currentPage?: number;
  onDelete: (product: IProductTableItem) => void;
  onSearch?: (value: string) => void;
  onPageChange?: (page: number) => void;
  totalPages?: number;
}

export const ProductTable = ({
  products,
  currentPage,
  onDelete,
  onSearch,
  onPageChange,
  totalPages,
  ...rest
}: IProductTableProps): JSX.Element => {
  const toast = useToast();

  const { assignment: authenticatedAssignment } = useAuth();

  const [handlingProduct, setHandlingProduct] = useState<IProductTableItem>();
  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);

  const toggleDeleteConfirmationModal = (product?: IProductTableItem): void => {
    setHandlingProduct(product);

    setIsDeleteConfirmationModalVisible((prevState) => !prevState);
  };

  const deleteProduct = async (product: IProductTableItem): Promise<void> => {
    try {
      await deleteProductsService(product.id);

      onDelete(product);

      toast({
        title: 'Excluído com sucesso',
        description: 'O produto foi excluído corretamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        variant: 'subtle',
        position: 'top-right',
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao excluir',
          description:
            translateError({ message: err.response?.data.message }) ||
            'Ocorreu um erro ao excluir o produto, tente novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    } finally {
      toggleDeleteConfirmationModal(undefined);
    }
  };

  return (
    <Box {...rest}>
      {!!handlingProduct && (
        <ConfirmationModal
          isOpen={!!isDeleteConfirmationModalVisible}
          onClose={() => toggleDeleteConfirmationModal(undefined)}
          onConfirm={() => deleteProduct(handlingProduct)}
          title="Confirmar exclusão"
          message="Deseja realmente excluir?"
        />
      )}

      <HStack align="flex-end" spacing="4">
        <TableFilters searchLabel="Buscar por nome" onSearch={onSearch} />
      </HStack>

      <Table colorScheme="blue" mt="4">
        <Thead>
          <Tr>
            <Th textAlign="center" w="4">
              Ativo
            </Th>

            <Th>Nome</Th>

            <Th textAlign="right" w="8" />
          </Tr>
        </Thead>

        <Tbody>
          {products.map((product) => (
            <Tr key={product.id}>
              <Td textAlign="center">
                {product.isActive ? (
                  <Icon as={RiCheckboxCircleLine} color="green" />
                ) : (
                  <Icon as={RiCheckboxBlankCircleLine} color="gray.400" />
                )}
              </Td>

              <Td fontWeight="bold">{product.name}</Td>

              <Td textAlign="right">
                <ButtonGroup>
                  {/* <LinkButton
                    px={2}
                    colorScheme="green"
                    to={{
                      pathname: '/services/appointments/management',
                      state: {
                        productId: product.id,
                      },
                    }}
                  >
                    <HStack spacing="1">
                      <Icon as={RiAddLine} fontSize="18" />
                      <Text>OS</Text>
                    </HStack>
                  </LinkButton> */}

                  <LinkButton
                    px={0}
                    to={{
                      pathname: '/services/products/details',
                      state: {
                        productId: product.id,
                      },
                    }}
                  >
                    <Icon as={RiEyeLine} fontSize="16" />
                  </LinkButton>

                  {authenticatedAssignment?.featureGroup.features.some(
                    (feature) =>
                      ['PRODUCT_FULL_ACCESS', 'PRODUCT_DELETE_ACCESS'].includes(
                        feature.key,
                      ),
                  ) && (
                    <IconButton
                      aria-label="delete-product"
                      size="sm"
                      icon={<Icon as={RiDeleteBinLine} fontSize="16" />}
                      colorScheme="red"
                      onClick={() => toggleDeleteConfirmationModal(product)}
                    />
                  )}
                </ButtonGroup>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!!currentPage && !!onPageChange && !!totalPages && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </Box>
  );
};
